import React, { useState, useEffect, useRef } from 'react'
import { css } from '@emotion/core'
import { Ellipsis, H2, Paragraph, useOverlay, WhiteTheme } from '@fs/zion-ui'
import { ContentCompose, NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import TextPickerOverlay from './TextPickerOverlay/TextPickerOverlay'
import { getMemory, handleFetchText } from '../../../../services/MemoriesAPI/memoriesAPI'
import { MissingMemoryIcon } from '../../../MemoryIconComponents/MemoryIconComponents'
import TextPickerSkeleton from './TextPickerSkeleton'
import { useStoryManager } from '../../../../providers/StoryProvider'
import { useMemoryBook } from '../../../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../../../helpers/getHasDarkBackground'

const TextPickerContainerCSS = (missingMemory) => css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  padding: 2px;
  gap: 12px;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0);
    border-radius: 5px;
    transition: background-color 0.3s ease-in-out;
  }

  &:focus {
    outline: 3px solid black;
    outline-offset: 2px;
  }

  ${!missingMemory &&
  `&:hover::after {
    background-color: rgba(0, 0, 0, 0.7);
  }
 `}
`

const floatingButtonCSS = css`
  color: black;
  position: absolute;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  background: none;
  border: none;
  padding: 0;
`

const clickableContainerCSS = css`
  all: unset; /* Reset button styles */
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  cursor: pointer;
`

const hoverOverlayCss = css`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-in-out;

  button:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`

const hoverOverlayAddTextContentCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const titleContainerCSS = css`
  height: 2.8em;
  line-height: 1.2;
`

// Render user-entered line breaks instead of collapsing them into spaces. This must be an inline
// style: Ellipsis's multiline class sets `white-space: normal`, which a className can't override.
const storyTextStyle = { whiteSpace: 'pre-line' }

const TextPicker = ({
  pageNumber,
  memoryId,
  positionIndex,
  isPrintView = false,
  customLayoutElementId,
  onUpdateCustomLayoutElementData,
  dimensions,
  isPlaceholder = false,
  onCancelPlaceholder,
}) => {
  const [t] = useTranslation()
  const textPickerOverlay = useOverlay()
  const { setTitle: setStoryTitle, setDescription, setStoryId, setPendingEdits, characterLimits } = useStoryManager()
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [isUpdating, setIsUpdating] = useState(false)
  const [missingMemory, setMissingMemory] = useState(false)

  const [loading, setLoading] = useState(false)
  const isMountedRef = useRef(true)

  // Track if overlay was auto-opened for placeholder elements
  const hasAutoOpenedRef = useRef(false)
  const prevIsOpenRef = useRef(false)

  const { lineCap } = characterLimits

  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  const handleMemoryUpdate = (updated = false) => {
    if (!isMountedRef.current) return
    setIsUpdating(updated)
    setRefreshKey((prevKey) => prevKey + 1)
  }

  useEffect(() => {
    let isMounted = true

    async function fetchMemories() {
      setLoading(true)
      setMissingMemory(false)
      try {
        if (memoryId) {
          const response = await getMemory(memoryId)

          if (response?.status === 404) {
            throw new Error('Memory not found')
          }
          const { title: storyTitle, url } = response

          if (isMounted) {
            const fetchedText = await handleFetchText(url, isUpdating)

            if (!isMounted) return

            setText(fetchedText)
            setTitle(storyTitle)
            setPendingEdits((prevEdits) => ({
              ...prevEdits,
              storyId: memoryId,
              title: storyTitle,
              description: fetchedText,
              isValidStory: true,
              isGalleryStory: true,
            }))
            setStoryId(memoryId)
            setStoryTitle(storyTitle)
            setDescription(fetchedText)

            setMissingMemory(false)
          }
        }
      } catch (e) {
        if (isMounted) {
          console.error('Failed to fetch memory:', e)
          setMissingMemory(true)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchMemories()

    return () => {
      isMounted = false
    }
  }, [memoryId, isUpdating, refreshKey, setPendingEdits, setStoryId, setStoryTitle, setDescription])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Auto-open overlay for placeholder elements
  useEffect(() => {
    if (isPlaceholder && !hasAutoOpenedRef.current && !memoryId && !loading) {
      hasAutoOpenedRef.current = true
      textPickerOverlay.handleClick()
    }
  }, [isPlaceholder, memoryId, loading, textPickerOverlay])

  // Detect cancellation (overlay closes without save)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    const isNowClosed = !textPickerOverlay.isOpen

    if (wasOpen && isNowClosed && isPlaceholder && !memoryId) {
      // User canceled without saving
      onCancelPlaceholder?.()
    }

    prevIsOpenRef.current = textPickerOverlay.isOpen
  }, [textPickerOverlay.isOpen, isPlaceholder, memoryId, onCancelPlaceholder])

  function getTextPickerAriaLabel() {
    if (missingMemory) {
      return t(
        'TextPicker.missingStoryMemory.label',
        'This story memory is missing, open the story editor to choose a new story.'
      )
    }
    if (title) {
      return t(
        'TextPicker.openEditorWithTitle.label',
        'Story titled: {title}, open the story editor to edit or choose a new story.',
        { title }
      )
    }
    return t('TextPicker.openEditor.label', 'Open Story Editor')
  }

  if (!memoryId && !loading) {
    if (isPrintView) {
      return null
    }

    return (
      <TextPickerSkeleton
        overlay={textPickerOverlay}
        positionIndex={positionIndex}
        pageNumber={pageNumber}
        handleMemoryUpdate={handleMemoryUpdate}
        customLayoutElementId={customLayoutElementId}
        onUpdateCustomLayoutElementData={onUpdateCustomLayoutElementData}
        dimensions={dimensions}
        characterLimits={characterLimits}
      />
    )
  }
  const buttonContent = (
    <>
      <div css={hoverOverlayCss}>
        <div css={hoverOverlayAddTextContentCss}>
          <ContentCompose size="md" />
          <Paragraph size="xs">{t('TextPicker.addText.label', 'Add Text')}</Paragraph>
        </div>
      </div>
      <div css={titleContainerCSS}>
        <H2 size="H5">
          <Ellipsis lineClamp={2}>{title}</Ellipsis>
        </H2>
      </div>
      <Paragraph size="sm">
        <Ellipsis lineClamp={lineCap} style={storyTextStyle}>
          {text}
        </Ellipsis>
      </Paragraph>
    </>
  )

  const renderButtonBody = () => {
    if (missingMemory && !isPrintView) {
      return (
        <MissingMemoryIcon
          label={t('TextPicker.missingMemoryIcon.label', 'This story is no longer in your memories.')}
        />
      )
    }
    if (hasDarkBackground) {
      return <WhiteTheme>{buttonContent}</WhiteTheme>
    }
    return buttonContent
  }

  return (
    <>
      <button
        css={[clickableContainerCSS, TextPickerContainerCSS(missingMemory)]}
        onClick={textPickerOverlay.handleClick}
        aria-label={getTextPickerAriaLabel()}
        type="button"
      >
        {loading ? (
          <div css={floatingButtonCSS}>
            <NoticeLoading size="md" color="black" />
          </div>
        ) : (
          renderButtonBody()
        )}
      </button>
      {(textPickerOverlay.isOpen || textPickerOverlay.transitioning) && (
        <TextPickerOverlay
          overlay={textPickerOverlay}
          positionIndex={positionIndex}
          pageNumber={pageNumber}
          handleMemoryUpdate={handleMemoryUpdate}
          customLayoutElementId={customLayoutElementId}
          onUpdateCustomLayoutElementData={onUpdateCustomLayoutElementData}
        />
      )}
    </>
  )
}

export default TextPicker
